import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { Box, Alert, CircularProgress } from '@mui/material';
import { PillButton } from '../../../components/superAdminUi';
import WizardShell from '../../../components/common/WizardShell';
import { loaderAction } from '../../../store/loaderStore';
import { matGroupService, schoolService, customerTypeService } from '../../../services';
import lookupService from '../../../services/api/lookupService';
import { DEFAULT_DATA_STORAGE_LOCATION } from '../../../constants/groupSchoolFieldOptions';
import { bulkStatusLabel } from '../../../constants/bulkSchoolStatus';
import { fromGias, fromBulkRow } from '../../../utils/schoolFormMapper';
import { withDuplicateFlags, findListDuplicate, schoolCompleteness, isNewSchoolRow } from './schoolsListUtils';
import { buildAddMatGroupPayload } from './payload';
import { extractServerErrors } from '../../../utils/serverErrors';
import GroupDuplicateWatch from '../../../components/common/GroupDuplicateWatch';
import GroupDetailsStep from './GroupDetailsStep';
import SubscriptionStep from './SubscriptionStep';
import SchoolsStep from './SchoolsStep';
import ReviewStep from './ReviewStep';
import SummaryRail from './SummaryRail';

const STEPS = ['Group details', 'Subscription & products', 'Schools', 'Review & create'];

// Only new-school rows (manual, file, or an edited GIAS pick) are gated on completeness;
// an attached existing school or an unedited GIAS pick is already a full record.
const isRowIncomplete = (row) => isNewSchoolRow(row) && !schoolCompleteness(row.values).complete;

const initialValues = {
  Group: '',
  CustomGroup: '',
  GroupName: '',
  Name: '',
  FirstName: '',
  LastName: '',
  Email: '',
  Username: '',
  GroupType: '',
  Country: '',
  DataStorageLocation: DEFAULT_DATA_STORAGE_LOCATION,
  CustomerType: '',
  File: null,
  FileName: null,
  TrancateFileName: null,
  isTestAccount: false,
  MatGroupUID: '',
  products: [],
  IsMATSubscription: true,
  IsMultiYear: false,
  SubscriptionTerm: 1,
  Year1Cost: 0,
  Year2Cost: 0,
  Year3Cost: 0,
  VatPercentApplied: 20,
  Discount: 0,
  PaymentMode: 1,
  IsAutoRenewal: true,
  StartDate: null,
  EndDate: null,
  RenewalDate: null,
};

function RegisterGroup(props) {
  const [activeStep, setActiveStep] = useState(0);
  const [storageLocations, setStorageLocations] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [schools, setSchools] = useState([]);
  const [giasOptions, setGiasOptions] = useState([]);
  const [existingSchoolOptions, setExistingSchoolOptions] = useState([]);
  const [giasGroupLoading, setGiasGroupLoading] = useState(false);
  const [giasGroupResult, setGiasGroupResult] = useState(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [groupDuplicate, setGroupDuplicate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdMatId, setCreatedMatId] = useState(null);
  const [reviewErrors, setReviewErrors] = useState([]);
  const rowKey = useRef(0);

  useEffect(() => {
    lookupService.getDataStorageLocations().then((res) => {
      const payload = res?.data ?? res;
      const raw = payload?.locations ?? payload?.Locations;
      if (Array.isArray(raw) && raw.length) {
        setStorageLocations(raw.map((o) => ({ value: o.value ?? o.Value, label: o.label ?? o.Label })));
      }
    });
    customerTypeService.getAllActiveCustomerTypes().then((response) => {
      if (response.success && response.data) setCustomerTypes(response.data);
    }).catch(() => {});
    // Existing Welbee schools available to attach (same source the standalone page used).
    schoolService.GetSchoolsForMatWithCloseSurvey().then((response) => {
      const data = response?.data ?? response;
      if (Array.isArray(data)) setExistingSchoolOptions(data);
    }).catch(() => {});
  }, []);

  const nextKey = () => {
    rowKey.current += 1;
    return `row-${rowKey.current}`;
  };

  const applyRows = (updater) => {
    setSchools((prev) => withDuplicateFlags(updater(prev)));
  };

  const addGiasSchool = (option) => {
    applyRows((prev) => {
      if (prev.some((row) => String(row.values.Urn) === String(option.URN))) return prev;
      const values = fromGias(option);
      if (!values.Country) values.Country = 'England';
      return [...prev, { id: nextKey(), source: 'gias', dirty: false, values }];
    });
  };

  const removeGiasSchool = (urn) => {
    applyRows((prev) => prev.filter((row) => !(row.source === 'gias' && String(row.values.Urn) === String(urn))));
  };

  const removeRow = (id) => {
    applyRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addManualSchool = (values) => {
    const reason = findListDuplicate(schools, values);
    if (reason) {
      toast.error(`A school with this ${reason} is already in the list.`);
      return false;
    }
    applyRows((prev) => [...prev, { id: nextKey(), source: 'manual', dirty: false, values }]);
    return true;
  };

  // Attach an existing Welbee school by search — folds in the retired standalone group
  // page's capability. Its DB id flows into MatSchoolIds; the row is a link, not a new school.
  const attachExistingSchool = (school) => {
    if (!school || !school.Id) return false;
    if (schools.some((row) => row.source === 'existing' && row.existingId === school.Id)) {
      toast.error('That school is already attached.');
      return false;
    }
    const rowValues = { Name: school.Name || '', Urn: school.WondeeSchoolUrn ? String(school.WondeeSchoolUrn) : '' };
    const reason = findListDuplicate(schools, rowValues);
    if (reason) {
      toast.error(`A school with this ${reason} is already in the list.`);
      return false;
    }
    applyRows((prev) => [...prev, { id: nextKey(), source: 'existing', existingId: school.Id, values: rowValues }]);
    return true;
  };

  const updateRow = (id, values) => {
    // Editing a row clears any server-side error pinned to it.
    applyRows((prev) => prev.map((row) => (
      row.id === id
        ? { ...row, values, serverError: null, dirty: row.source === 'gias' ? true : row.dirty }
        : row
    )));
  };

  const handleFileSelected = (file) => {
    setFileBusy(true);
    schoolService.bulkAddSchools(file, { dryRun: true }).then(({ success, message, result }) => {
      if (!success || !result) {
        toast.error(message || 'The file could not be processed.');
        return;
      }
      let added = 0;
      let skipped = 0;
      applyRows((prev) => {
        const next = [...prev];
        result.rows.forEach((row) => {
          const label = bulkStatusLabel(row.status);
          if (label !== 'Added' || !row.fields) {
            skipped += 1;
            return;
          }
          const values = fromBulkRow(row.fields);
          if (findListDuplicate(next, values)) {
            skipped += 1;
            return;
          }
          next.push({ id: nextKey(), source: 'file', dirty: false, values });
          added += 1;
        });
        return next;
      });
      toast.success(`${added} school(s) added from file.${skipped ? ` ${skipped} row(s) skipped (duplicate or invalid).` : ''}`);
    }).catch((err) => {
      // The service rejects with the parsed API error body ({ success:false, message }) —
      // surface its message (bad workbook, oversized file, …) rather than a blanket line.
      toast.error((err?.success === false && err.message) || 'Failed to read the file.');
    }).finally(() => {
      setFileBusy(false);
    });
  };

  const searchGiasGroup = (formik) => {
    const guid = (formik.values.MatGroupUID || '').trim();
    if (!guid) {
      toast.error('Please enter Group GUID');
      return;
    }
    setGiasGroupLoading(true);
    setGiasGroupResult(null);

    // Resolve the group's details first (for autofill + the confirmation name), then load
    // its schools, so the green confirmation can name the trust it found.
    schoolService.GetMatGroupDetailsFromGIAS(guid).then((groupResponse) => {
      let name = formik.values.GroupName || formik.values.Name || '';
      if (groupResponse.success && groupResponse.data) {
        const details = groupResponse.data;
        name = details.GroupName || details.Name || name;
        if (!formik.values.GroupName) formik.setFieldValue('GroupName', details.GroupName || '');
        if (!formik.values.Name) formik.setFieldValue('Name', details.Name || '');
        if (!formik.values.GroupType) formik.setFieldValue('GroupType', details.EstablishmentTypeGroupName || '');
      }
      return name;
    }).catch(() => (formik.values.GroupName || formik.values.Name || '')).then((name) => {
      schoolService.GetSchoolForMATGUID(guid).then((response) => {
        if (response.success && response.data && response.data.length > 0) {
          setGiasOptions(response.data);
          setGiasGroupResult({ name, count: response.data.length });
          toast.success(`Found ${response.data.length} school(s) for this MAT Group.`);
        } else {
          setGiasOptions([]);
          setGiasGroupResult({ name, count: 0 });
          toast.error('No schools found for this MAT Group UID');
        }
        setGiasGroupLoading(false);
      }).catch(() => {
        toast.error('Error fetching schools');
        setGiasGroupLoading(false);
      });
    });
  };

  const mapServerErrors = (errors) => {
    const groupScoped = [];
    setSchools((prev) => {
      const next = prev.map((row) => ({ ...row, serverError: null }));
      errors.forEach((err) => {
        const identifier = (err.identifier || '').toString().toLowerCase();
        const target = next.find((row) =>
          String(row.values.Urn).toLowerCase() === identifier ||
          (row.values.Name || '').toLowerCase() === identifier
        );
        if (target) {
          target.serverError = err.message;
        } else {
          groupScoped.push(err.message);
        }
      });
      return next;
    });
    setReviewErrors(groupScoped.length ? groupScoped : errors.map((e) => e.message));
  };

  const handleCreate = (formik) => {
    if (schools.some((row) => row.duplicate)) {
      toast.error('Resolve the duplicate schools before creating the group.');
      return;
    }
    if (schools.some(isRowIncomplete)) {
      toast.error('Complete the highlighted schools first.');
      return;
    }
    setReviewErrors([]);
    setSubmitting(true);
    const payload = buildAddMatGroupPayload(formik.values, schools);
    props.loaderActions.dataLoading(true);
    matGroupService.addMatGroup(payload).then((response) => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      const { success, data } = response || {};
      if (success) {
        toast.success('Group added successfully');
        const matId = data?.matId ?? data?.Id ?? data?.data?.Id ?? data?.id;
        if (payload.StartDate && matId) {
          schoolService.generateInvoiceForSubscription({
            MatId: matId,
            YearNumber: 1,
            RenewalDateOverride: payload.RenewalDate ? new Date(payload.RenewalDate).toISOString() : null,
          }).catch(() => {});
        }
        setCreatedMatId(matId || null);
      } else {
        const errors = extractServerErrors(response);
        if (errors.length) {
          mapServerErrors(errors);
        } else {
          setReviewErrors([response?.message || 'Failed to create the group.']);
        }
      }
    }).catch(() => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      setReviewErrors(['Failed to create the group.']);
    });
  };

  const goToGroup = () => {
    if (createdMatId) {
      props.history.push(`/group-detail?matId=${createdMatId}`);
    }
  };

  const canLeaveStep = (formik, step) => {
    if (step === 0) {
      if (!formik.values.Group || !formik.values.Name || formik.values.Name.trim() === '') {
        toast.error('Please choose a group and enter a group name.');
        return false;
      }
      // "Other" requires a resolved custom group name (GroupName mirrors it).
      if (!formik.values.GroupName || formik.values.GroupName.trim() === '') {
        toast.error('Please enter a custom group name.');
        return false;
      }
      if (groupDuplicate && groupDuplicate.isDuplicate) {
        toast.error('A group with this name or GUID already exists.');
        return false;
      }
    }
    if (step === 2) {
      if (schools.some((row) => row.duplicate)) {
        toast.error('Resolve the duplicate schools before continuing.');
        return false;
      }
      if (schools.some(isRowIncomplete)) {
        toast.error('Complete the highlighted schools first.');
        return false;
      }
    }
    return true;
  };

  return (
    <Formik initialValues={initialValues} onSubmit={() => {}}>
      {(formik) => {
        // Group-scoped create errors are pinned here until the operator dismisses them,
        // edits the group name, or resubmits — never auto-dismissed, never touched by the
        // duplicate-check effect.
        const banner = reviewErrors.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {reviewErrors.map((message, index) => (
              <Alert
                key={index}
                severity="error"
                sx={{ mb: 0.5 }}
                onClose={() => setReviewErrors((prev) => prev.filter((_, i) => i !== index))}
              >
                {message}
              </Alert>
            ))}
          </Box>
        ) : null;

        const finalAction = submitting
          ? <CircularProgress size={32} />
          : (
            <PillButton type="button" $variant="danger" $size="lg" onClick={() => handleCreate(formik)}>
              Create group
            </PillButton>
          );

        return (
          <div>
            <GroupDuplicateWatch
              name={formik.values.Name}
              groupUid={formik.values.MatGroupUID}
              onResult={setGroupDuplicate}
            />
            <WizardShell
              title="Register New Group"
              steps={STEPS}
              activeStep={activeStep}
              onStepSelect={(index) => setActiveStep(index)}
              banner={banner}
              hideFooter={createdMatId !== null}
              onBack={() => setActiveStep((s) => s - 1)}
              onNext={() => { if (canLeaveStep(formik, activeStep)) setActiveStep((s) => s + 1); }}
              finalAction={finalAction}
              summary={(
                <SummaryRail
                  values={formik.values}
                  schools={schools}
                  warnings={groupDuplicate && groupDuplicate.isDuplicate ? ['Duplicate group name/GUID'] : []}
                />
              )}
            >
              {activeStep === 0 && (
                <GroupDetailsStep
                  formik={formik}
                  customerTypes={customerTypes}
                  storageLocations={storageLocations}
                  duplicate={groupDuplicate}
                  giasLoading={giasGroupLoading}
                  giasResult={giasGroupResult}
                  onGiasGroupSearch={() => searchGiasGroup(formik)}
                  onGroupFieldEdit={() => setReviewErrors([])}
                />
              )}
              {activeStep === 1 && <SubscriptionStep formik={formik} />}
              {activeStep === 2 && (
                <SchoolsStep
                  rows={schools}
                  giasOptions={giasOptions}
                  existingSchoolOptions={existingSchoolOptions}
                  customerTypes={customerTypes}
                  onAddGias={addGiasSchool}
                  onRemoveGias={removeGiasSchool}
                  onRemoveRow={removeRow}
                  onAddManual={addManualSchool}
                  onAttachExisting={attachExistingSchool}
                  onUpdateRow={updateRow}
                  onFileSelected={handleFileSelected}
                  fileBusy={fileBusy}
                />
              )}
              {activeStep === 3 && (
                <ReviewStep
                  values={formik.values}
                  schools={schools}
                  created={createdMatId !== null}
                  onGoToGroup={goToGroup}
                />
              )}
            </WizardShell>
          </div>
        );
      }}
    </Formik>
  );
}

const mapDispatchToProps = (dispatch) => ({
  loaderActions: bindActionCreators(loaderAction, dispatch),
});

export default connect(null, mapDispatchToProps)(withRouter(RegisterGroup));
